import React from "react";
// ANT DESIGN COMPONENTS
import { InputNumber, Radio, Select, Switch, Tooltip } from "antd";
// COMPONENTS
import FormInputNumber from "../../../../components/form/FormInputNumber";
// FLATBUFFER ENUMS
import { FrameType } from "../../../../../flatbuffers/generated/lobby-vision2/frame-type";
import { OperationType } from "../../../../../flatbuffers/generated/lobby-vision2/operation-type";
import { SecureCarCalltype } from "../../../../../flatbuffers/generated/lobby-vision2/secure-car-calltype";
// INTERFACES
import type {
  LobbyVision2Addressing,
  LobbyVision2LiftNumSource,
} from "../../../../../interfaces/AdapterCdoLobbyVision2.interface";
import {
  ccTypeForCallModeChange,
  isLobbyVision2CallOperation,
  isValidSecureCarCalltype,
} from "../../../../../interfaces/AdapterCdoLobbyVision2.interface";

const { Option } = Select;

/**
 * Build `{ value, label }` dropdown options from a generated numeric FlatBuffer
 * enum, skipping the reverse-mapping string keys. Keeps the two enum-backed
 * selects in this file (`OperationType`, `SecureCarCalltype`) from repeating the
 * same `Object.entries → filter numeric → map` pipeline.
 */
function enumNumericOptions<T extends number>(
  enumObj: Record<string, string | number>,
): { value: T; label: string }[] {
  return Object.entries(enumObj)
    .filter((entry): entry is [string, number] => typeof entry[1] === "number")
    .map(([label, value]) => ({ value: value as T, label }));
}

/** FrameType arms valid for the ln2→msg (Request) direction. */
export const REQUEST_FRAME_TYPES: FrameType[] = [
  FrameType.statusRequest,
  FrameType.changeRequest,
  FrameType.commandRequest,
];

/** FrameType arms valid for the msg→ln2 (Response) direction. */
export const RESPONSE_FRAME_TYPES: FrameType[] = [
  FrameType.secureCCResponse,
  FrameType.statusResponse,
];

const FRAME_TYPE_LABELS: Record<FrameType, string> = {
  [FrameType.unknown]: "unknown",
  [FrameType.login]: "login",
  [FrameType.statusRequest]: "statusRequest",
  [FrameType.secureCCResponse]: "secureCCResponse",
  [FrameType.changeRequest]: "changeRequest",
  [FrameType.statusResponse]: "statusResponse",
  [FrameType.commandRequest]: "commandRequest",
};

const OPERATION_TYPE_OPTIONS = enumNumericOptions<OperationType>(OperationType);

/**
 * Options for the `SecureCarCalltype` selector, built from the generated enum
 * so new arms surface automatically. These are the "any type of Car Call,
 * Hall Call, or secure car call" the wire distinguishes: front/rear car call
 * and up/down front/rear hall call.
 */
const SECURE_CAR_CALLTYPE_OPTIONS = enumNumericOptions<SecureCarCalltype>(SecureCarCalltype);

/** Dropdown over the generated FrameType enum, filtered to the arms valid for one direction. */
export function FrameTypeSelect({
  value,
  onChange,
  allowedTypes,
  label = "Frame Type",
}: {
  value: FrameType;
  onChange: (value: FrameType) => void;
  allowedTypes: FrameType[];
  label?: string;
}) {
  return (
    <div>
      <span className="generalStyles__inputLabel">{label}</span>
      <Select className="generalStyles__width100" size="large" value={value} onChange={onChange}>
        {allowedTypes.map((ft) => (
          <Option key={ft} value={ft}>
            {FRAME_TYPE_LABELS[ft]}
          </Option>
        ))}
      </Select>
    </div>
  );
}

/** Dropdown over the generated OperationType enum. */
export function OperationTypeSelect({
  value,
  onChange,
  label = "Operation Type",
}: {
  value: OperationType;
  onChange: (value: OperationType) => void;
  label?: string;
}) {
  return (
    <div>
      <span className="generalStyles__inputLabel">{label}</span>
      <Select className="generalStyles__width100" size="large" value={value} onChange={onChange}>
        {OPERATION_TYPE_OPTIONS.map((opt) => (
          <Option key={opt.value} value={opt.value}>
            {opt.label}
          </Option>
        ))}
      </Select>
    </div>
  );
}

/**
 * Required dropdown over the generated `SecureCarCalltype` enum. Rendered
 * alongside the raw `data` byte input whenever the row's `OperationType` is a
 * call (carcall / alloc_cc / secure_cc) or the mapped LN2 message is itself a
 * call. The selected arm (1..6) is written into the row's dedicated `ccType`
 * field, independent of `data` (which stays free for floor/raw-data use). A
 * `ccType` of `0` means "not yet chosen"; the field shows a required hint
 * until a real arm is picked (enforced at Add-time and at publish-validation).
 */
export function SecureCarCalltypeSelect({
  value,
  onChange,
  label = "Call Type",
}: {
  value: number;
  onChange: (value: number) => void;
  label?: string;
}) {
  const chosen = isValidSecureCarCalltype(value) ? value : undefined;
  return (
    <div>
      <span className="generalStyles__inputLabel">
        {label}
        {chosen === undefined && <span style={{ color: "#ff4d4f" }}> *required</span>}
      </span>
      <Select
        className="generalStyles__width100"
        size="large"
        value={chosen}
        placeholder="Select call type"
        status={chosen === undefined ? "error" : undefined}
        onChange={(next) => onChange(next)}
      >
        {SECURE_CAR_CALLTYPE_OPTIONS.map((opt) => (
          <Option key={opt.value} value={opt.value}>
            {opt.label}
          </Option>
        ))}
      </Select>
    </div>
  );
}

/**
 * The rightmost telegraph column(s). Renders the plain `data` byte input
 * (greyed to "Auto" when the row is `floorsBased`, since data is derived
 * per-floor at expansion) and, for a call mapping (a call `OperationType` OR
 * a call LN2 message), ALSO renders the required `SecureCarCalltype`
 * selector bound to the row's dedicated `ccType` field. The two are
 * independent fields on the wire, so a call mapping can freely be
 * `floorsBased` too — `data` carries the floor, `ccType` carries the call
 * type, and neither overwrites the other at CDO-generation time.
 */
function LobbyVision2DataColumn({
  operationType,
  messageIsCall,
  floorsBased,
  data,
  onChange,
  ccType,
  onCcTypeChange,
}: {
  operationType: OperationType;
  messageIsCall: boolean;
  floorsBased: boolean;
  data: number;
  onChange: (data: number) => void;
  ccType: number;
  onCcTypeChange: (ccType: number) => void;
}) {
  const isCallRow = isLobbyVision2CallOperation(operationType) || messageIsCall;
  return (
    <>
      <span className="generalStyles__inputLabel">
        Data
        {floorsBased && <span style={{ opacity: 0.5 }}> (auto)</span>}
      </span>
      <div style={{ marginTop: 8 }}>
        {floorsBased ? (
          <span style={{ opacity: 0.5, fontSize: 11 }}>Auto (per-floor at expansion)</span>
        ) : (
          <InputNumber
            className="generalStyles__width100"
            size="large"
            min={0}
            max={255}
            value={data}
            onChange={(next) => onChange(next ?? 0)}
          />
        )}
      </div>
      {isCallRow && (
        <div style={{ marginTop: 8 }}>
          <SecureCarCalltypeSelect value={ccType} onChange={onCcTypeChange} />
        </div>
      )}
    </>
  );
}

/**
 * The telegraph-op fields shared by both mapping directions: FrameType
 * (filtered per direction), OperationType, operationId, and data. These
 * fields are authored VERBATIM on the template — they are not expanded
 * per-car/per-floor (only lift_num/bank_num/data-when-floors-based are
 * derived by LiftCloud at CDO-generation time). `data` is greyed out and
 * shows "Auto" when `floorsBased` is set (mirrors LN1/Modbus), since the
 * expander overwrites it per floor.
 *
 * Telegraph `bank_num`/`lift_num` are intentionally NOT rendered here —
 * the template is bank/lift-agnostic; those are derived at expansion time
 * from the adapter's addressing config + the assigned bank/lift.
 */
export default function LobbyVision2TelegraphFields({
  value,
  onChange,
  allowedFrameTypes,
  floorsBased,
  messageIsCall,
}: {
  value: {
    telegraphType: FrameType;
    operationId: number;
    operationType: OperationType;
    data: number;
    ccType: number;
  };
  onChange: (value: {
    telegraphType: FrameType;
    operationId: number;
    operationType: OperationType;
    data: number;
    ccType: number;
  }) => void;
  allowedFrameTypes: FrameType[];
  floorsBased: boolean;
  /** True when the row's mapped LN2 message is itself a call — makes `ccType` required even for a non-call operationType. */
  messageIsCall: boolean;
}) {
  return (
    <div className="row mt-2">
      <div className="col-12 col-md-6 mt-2">
        <FrameTypeSelect
          value={value.telegraphType}
          onChange={(telegraphType) => onChange({ ...value, telegraphType })}
          allowedTypes={allowedFrameTypes}
        />
      </div>
      <div className="col-12 col-md-6 mt-2">
        <OperationTypeSelect
          value={value.operationType}
          onChange={(operationType) =>
            onChange({
              ...value,
              operationType,
              // Reset ccType whenever call-mode flips, so a stale chosen call
              // type is not silently carried over into a different call (or
              // left set on a row that is no longer a call). `data` is a
              // plain byte independent of call-mode and is left untouched.
              // `messageIsCall` is constant across an operationType change,
              // so it appears on both sides.
              ccType: ccTypeForCallModeChange(
                isLobbyVision2CallOperation(value.operationType) || messageIsCall,
                isLobbyVision2CallOperation(operationType) || messageIsCall,
                value.ccType,
              ),
            })
          }
        />
      </div>
      <div className="col-12 col-md-6">
        <FormInputNumber
          inputValue={value.operationId}
          inputLabelName="Operation ID"
          inputVariableName="operationId"
          inputOnChange={(operationId) => onChange({ ...value, operationId })}
        />
      </div>
      <div className="col-12 col-md-6">
        <LobbyVision2DataColumn
          operationType={value.operationType}
          messageIsCall={messageIsCall}
          floorsBased={floorsBased}
          data={value.data}
          onChange={(data) => onChange({ ...value, data })}
          ccType={value.ccType}
          onCcTypeChange={(ccType) => onChange({ ...value, ccType })}
        />
      </div>
    </div>
  );
}

/**
 * The four LN1-style expansion toggles shared by read/write rows: Unit
 * (unitsBased), Floor (floorsBased), Bank (bankLevel), Own (individual).
 * Mirrors `CdoLn1MappingEditor`'s toggle UI/tooltips. `bankLevel` wins over
 * `individual` when both are set (see expansion contract, §D#6 of the
 * redesign spec) — reflected here only in the tooltip copy, matching how
 * LN1's own toggle row does not disable the switches against each other.
 *
 * Floor is fully independent of call mappings: `ccType` (the call type) and
 * `data` (the floor, when `floorsBased`) are separate fields on the wire, so
 * a call row can be floor-expanded too — there is no longer any exclusion to
 * enforce here.
 */
export function LobbyVision2ExpansionToggles({
  unitsBased,
  floorsBased,
  bankLevel,
  individual,
  onChange,
}: {
  unitsBased: boolean;
  floorsBased: boolean;
  bankLevel: boolean;
  individual: boolean;
  onChange: (
    patch: Partial<{
      unitsBased: boolean;
      floorsBased: boolean;
      bankLevel: boolean;
      individual: boolean;
    }>,
  ) => void;
}) {
  return (
    <div className="row mt-2">
      <div className="col-6 col-md-3 mt-2">
        <Tooltip title="Duplicated per lift (unitsBased) — one telegraph per car in the bank">
          <span className="generalStyles__inputLabel">Unit</span>
        </Tooltip>
        <div style={{ marginTop: 8 }}>
          <Switch checked={unitsBased} onChange={(checked) => onChange({ unitsBased: checked })} />
        </div>
      </div>
      <div className="col-6 col-md-3 mt-2">
        <Tooltip title="Duplicated per floor (floorsBased) — data is derived per floor at expansion time">
          <span className="generalStyles__inputLabel">Floor</span>
        </Tooltip>
        <div style={{ marginTop: 8 }}>
          <Switch
            checked={floorsBased}
            onChange={(checked) => onChange({ floorsBased: checked })}
          />
        </div>
      </div>
      <div className="col-6 col-md-3 mt-2">
        <Tooltip title="Targets the whole bank (bankLevel) — lift_id = bank_id, lift_num = addressing.bankLiftNum. Wins over Own when both are set.">
          <span className="generalStyles__inputLabel">Bank</span>
        </Tooltip>
        <div style={{ marginTop: 8 }}>
          <Switch checked={bankLevel} onChange={(checked) => onChange({ bankLevel: checked })} />
        </div>
      </div>
      <div className="col-6 col-md-3 mt-2">
        <Tooltip title="Expands on its own addressing lane (individual) instead of sharing the adapter's default lift numbering">
          <span className="generalStyles__inputLabel">Own</span>
        </Tooltip>
        <div style={{ marginTop: 8 }}>
          <Switch checked={individual} onChange={(checked) => onChange({ individual: checked })} />
        </div>
      </div>
    </div>
  );
}

/**
 * Per-row lift_num source override — rendered ONLY when the row's `individual`
 * toggle is on (see `LobbyVision2ExpansionToggles`'s "Own" switch). Reuses
 * the exact Radio.Group markup + options from `LobbyVision2AddressingPanel`'s
 * adapter-level Lift Number Source selector, bound to the row's
 * `liftNumSourceOverride` instead of the adapter's `addressing.liftNumSource`.
 * When `liftNumSourceOverride` is "index", also reveals a small Lift Number
 * Base override input (mirroring the addressing panel's Lift Number Base).
 * Returns null when `individual` is false — callers gate rendering on this
 * return value rather than nesting a ternary inline.
 */
export function LobbyVision2RowLiftNumOverride({
  individual,
  liftNumSourceOverride,
  liftNumBaseOverride,
  onChange,
}: {
  individual: boolean;
  liftNumSourceOverride: LobbyVision2LiftNumSource;
  liftNumBaseOverride: number;
  onChange: (
    patch: Partial<{
      liftNumSourceOverride: LobbyVision2LiftNumSource;
      liftNumBaseOverride: number;
    }>,
  ) => void;
}) {
  if (!individual) return null;

  const isIndexBased = liftNumSourceOverride === "index";

  return (
    <div className="row mt-2">
      <div className="col-12 col-md-6 mt-2">
        <Tooltip title="Per-row lift_num source, used only while Own is on — overrides the adapter's default Lift Number Source for this row">
          <span className="generalStyles__inputLabel">Row Lift Number Source</span>
        </Tooltip>
        <div style={{ marginTop: 8 }}>
          <Radio.Group
            value={liftNumSourceOverride}
            onChange={(e) =>
              onChange({ liftNumSourceOverride: e.target.value as LobbyVision2LiftNumSource })
            }
            options={[
              { label: "Index-based", value: "index" },
              { label: "Lift Position", value: "liftPosition" },
              { label: "Car Number", value: "carNum" },
            ]}
          />
        </div>
      </div>
      <div className="col-12 col-md-6 mt-2">
        <span className="generalStyles__inputLabel">
          Row Lift Number Base
          {!isIndexBased && <span style={{ opacity: 0.5 }}> (n/a)</span>}
        </span>
        <div style={{ marginTop: 8 }}>
          <InputNumber
            className="generalStyles__width100"
            size="large"
            min={0}
            max={255}
            disabled={!isIndexBased}
            value={liftNumBaseOverride}
            onChange={(value) => onChange({ liftNumBaseOverride: value ?? 1 })}
          />
        </div>
      </div>
    </div>
  );
}

/** Human-readable per-row explainer mirroring Modbus's `buildExpansionMessage`. */
export function buildLobbyVision2ExpansionMessage(
  unitsBased: boolean,
  floorsBased: boolean,
  bankLevel: boolean,
  individual: boolean,
): string | null {
  const parts: string[] = [];
  if (bankLevel) {
    parts.push(
      "Targets the bank as a whole (lift_num = addressing.bankLiftNum, lift_id = bank_id)",
    );
  } else if (individual) {
    parts.push("Expands on its own addressing lane, not merged with sibling entries");
  }
  if (unitsBased) {
    parts.push("Duplicated across all lifts using the adapter's lift_num addressing");
  }
  if (floorsBased) {
    parts.push("Expands per floor at generation time (bank.FloorCount entries)");
  }
  return parts.length > 0 ? parts.join(". ") : null;
}

/** Describe the resolved lift_num derivation for the live-preview line — one branch per `LobbyVision2LiftNumSource` (extracted to avoid a nested ternary). */
function describeLiftNumPreview(value: LobbyVision2Addressing): string {
  if (value.liftNumSource === "liftPosition") {
    return "lift_num = Lift.Position (per-car ordinal)";
  }
  if (value.liftNumSource === "carNum") {
    return "lift_num = Lift.car_num (explicit per-lift car number)";
  }
  return `lift_num = ${value.liftNumBase} + car_index → ${value.liftNumBase},${
    value.liftNumBase + 1
  },${value.liftNumBase + 2}…`;
}

/**
 * Per-adapter addressing configuration panel — rendered ONCE at the top of
 * `CdoLobbyVision2Page`, mirroring the placement of Modbus's global
 * settings block (Slave ID / Polling Interval rendered once above the
 * mapping tables).
 */
export function LobbyVision2AddressingPanel({
  value,
  onChange,
}: {
  value: LobbyVision2Addressing;
  onChange: (value: LobbyVision2Addressing) => void;
}) {
  const isIndexBased = value.liftNumSource === "index";
  const previewLiftNum = describeLiftNumPreview(value);
  const previewFloorData = `floor data = ${value.floorDataBase} + floor_index × ${
    value.floorDataStep
  } → ${value.floorDataBase},${
    value.floorDataBase + value.floorDataStep
  },${value.floorDataBase + 2 * value.floorDataStep}…`;

  return (
    <div className="container mb-3">
      <div className="row">
        <div className="col-12 col-md-4 mt-3">
          <span className="generalStyles__inputLabel">Lift Number Source</span>
          <div style={{ marginTop: 8 }}>
            <Radio.Group
              value={value.liftNumSource}
              onChange={(e) =>
                onChange({ ...value, liftNumSource: e.target.value as LobbyVision2LiftNumSource })
              }
              options={[
                { label: "Index-based", value: "index" },
                { label: "Lift Position", value: "liftPosition" },
                { label: "Car Number", value: "carNum" },
              ]}
            />
          </div>
        </div>
        <div className="col-12 col-md-4 mt-3">
          <span className="generalStyles__inputLabel">
            Lift Number Base
            {!isIndexBased && <span style={{ opacity: 0.5 }}> (n/a)</span>}
          </span>
          <div style={{ marginTop: 8 }}>
            <InputNumber
              className="generalStyles__width100"
              size="large"
              min={0}
              max={255}
              disabled={!isIndexBased}
              value={value.liftNumBase}
              onChange={(liftNumBase) => onChange({ ...value, liftNumBase: liftNumBase ?? 1 })}
            />
          </div>
        </div>
        <div className="col-12 col-md-4 mt-3">
          <Tooltip title="Sentinel lift_num emitted for bank-level entries (whole-bank telegraph)">
            <span className="generalStyles__inputLabel">Bank Sentinel lift_num</span>
          </Tooltip>
          <div style={{ marginTop: 8 }}>
            <InputNumber
              className="generalStyles__width100"
              size="large"
              min={0}
              max={255}
              value={value.bankLiftNum}
              onChange={(bankLiftNum) => onChange({ ...value, bankLiftNum: bankLiftNum ?? 0 })}
            />
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-4 mt-3">
          <span className="generalStyles__inputLabel">Floor Data Base</span>
          <div style={{ marginTop: 8 }}>
            <InputNumber
              className="generalStyles__width100"
              size="large"
              min={0}
              max={255}
              value={value.floorDataBase}
              onChange={(floorDataBase) =>
                onChange({ ...value, floorDataBase: floorDataBase ?? 0 })
              }
            />
          </div>
        </div>
        <div className="col-12 col-md-4 mt-3">
          <span className="generalStyles__inputLabel">Floor Data Step</span>
          <div style={{ marginTop: 8 }}>
            <InputNumber
              className="generalStyles__width100"
              size="large"
              min={0}
              max={255}
              value={value.floorDataStep}
              onChange={(floorDataStep) =>
                onChange({ ...value, floorDataStep: floorDataStep ?? 1 })
              }
            />
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-12 mt-2">
          <span style={{ opacity: 0.6, fontSize: 12 }}>
            {previewLiftNum}; {previewFloorData}
          </span>
        </div>
      </div>
    </div>
  );
}
